import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import { Stack, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  cloneDeep,
  difference,
  every,
  includes,
  mapValues,
  sortBy,
  union,
} from 'lodash';
import CustomCheckbox from 'components/Form/Checkbox/Checkbox';
import NoRecordsFound from 'components/ListView/EmptyRecords/noRecordsFound.cmp';
import CustomInput from 'components/Form/TextField/TextField.cmp';
import useCustomRoles from 'utils/CustomHooks/useCustomRoles';
import {
  PERMISSIONS,
  PERMISSIONS_ENTITY_KEYWORDS,
  TASKS_PERMISSIONS_ORDER,
  TEMPLATES_PERMISSIONS_ORDER,
  USER_PERMISSIONS_ORDER,
  WORK_REQUESTS_PERMISSIONS_ORDER,
  ANNOUNCEMENTS_PERMISSIONS_ORDER,
  WORK_ORDERS_PERMISSIONS_ORDER,
  LOCATIONS_PERMISSIONS_ORDER,
  ROUTING_RULES_PERMISSIONS_ORDER,
} from 'utils/constants';
import { getPermissionsForRoles } from 'store/selectors/permissions';
import { QUERY_KEYS } from 'api/constants';
import {
  EmptyCnt,
  MainPermission,
  PermissionsListingCnt,
  SearchBox,
  SubPermission,
} from './style';
import InnerBanner from './generateBanner';
import {
  updatePermissionsState,
  updateViewWorkOrdersPermissions,
  updateWorkOrdersPermissionsState,
} from './utils';
import {
  HIDE_LINEAR_GROUPS,
  HIDE_LINEAR_GROUPS_WITH_FEATURE_FLAG,
  PERMISSIONS_HIERARCHY,
} from './constants';
import { useCustomPermission } from 'utils/CustomHooks/useCustomPermission';
type Permission = string | number;

interface CustomRolesSetPermissionsListingProps {
  selected: Permission[];
  onSelectedChange?: (selected: Permission[]) => void;
  isSearch?: boolean; // Added isSearch prop
  height?: any;
  isEditable?: boolean;
}

const CustomRolesSetPermissionsListing: React.FC<
  CustomRolesSetPermissionsListingProps
> = ({
  selected = [],
  height = 400,
  onSelectedChange,
  isSearch = true, // Default to true if not provided
  isEditable = false,
}) => {
  const [selectedState, setSelectedState] = useState<Permission[]>(selected);
  const [searchedTerm, setSearchedTerm] = useState('');
  const [filteredPermissionsList, setFilteredPermissionsList] = useState<any>(
    {},
  );
  const permissions = useSelector(getPermissionsForRoles);
  const { list: permissionsList } = permissions || {};
  const { getPermissions } = useCustomRoles();
  const { hasFeature } = useCustomPermission();

  useQuery(QUERY_KEYS.GET_PERMISSIONS, getPermissions);

  useEffect(() => {
    const clonedList = cloneDeep(permissionsList);

    setFilteredPermissionsList(clonedList ?? {}); // Initialize filtered list with all permissions
  }, [permissionsList]);

  useEffect(() => {
    setSelectedState(selected);
  }, [selected]);

  const parentChangeHandler = (
    parentKey: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = event.target.checked;
    const updatedList = isChecked
      ? union(
          permissionsList[parentKey].map((item) => item.id),
          selectedState,
        )
      : difference(
          selectedState,
          permissionsList[parentKey].map((item) => item.id),
        );
    setSelectedState(updatedList);
    onSelectedChange?.(updatedList);
  };

  const updateTasksPermissionsState = (data) => {
    let clonedData = data;
    const CanAccessTasks = filteredPermissionsList?.['TASK']?.find(
      (item) => item?.title === PERMISSIONS.CAN_VIEW_TASKS,
    );

    const isCanAccessTasks = data?.includes(CanAccessTasks?.id);
    if (!isCanAccessTasks) {
      const CanOnlyViewOwnTasks = filteredPermissionsList?.['TASK']?.find(
        (item) => item?.title === PERMISSIONS.CAN_ONLY_VIEW_OWN_TASKS,
      );

      clonedData = clonedData?.filter((id) => id != CanOnlyViewOwnTasks?.id);

      const CanViewOwnAndOthersTasks = filteredPermissionsList?.['TASK']?.find(
        (item) =>
          item?.title === PERMISSIONS.CAN_VIEW_OTHERS_TASKS_AT_OWN_LOCATIONS,
      );

      clonedData = clonedData?.filter(
        (id) => id != CanViewOwnAndOthersTasks?.id,
      );

      const CanViewAllTasks = filteredPermissionsList?.['TASK']?.find(
        (item) => item?.title === PERMISSIONS.CAN_VIEW_OTHERS_TASKS,
      );

      clonedData = clonedData?.filter((id) => id != CanViewAllTasks?.id);
    }

    const CanOnlyViewOwnTasks = filteredPermissionsList?.['TASK']?.find(
      (item) => item?.title === PERMISSIONS.CAN_ONLY_VIEW_OWN_TASKS,
    );

    const CanViewOwnAndOthersTasks = filteredPermissionsList?.['TASK']?.find(
      (item) =>
        item?.title === PERMISSIONS.CAN_VIEW_OTHERS_TASKS_AT_OWN_LOCATIONS,
    );

    const CanViewAllTasks = filteredPermissionsList?.['TASK']?.find(
      (item) => item?.title === PERMISSIONS.CAN_VIEW_OTHERS_TASKS,
    );

    const isNotExists =
      !data?.includes(CanOnlyViewOwnTasks?.id) &&
      !data?.includes(CanViewOwnAndOthersTasks?.id) &&
      !data?.includes(CanViewAllTasks?.id);

    if (isNotExists || !isCanAccessTasks) {
      const CanManageTasks = filteredPermissionsList?.['TASK']?.find(
        (item) => item?.title === PERMISSIONS.CAN_MANAGE_TASKS,
      );

      clonedData = clonedData?.filter((id) => id != CanManageTasks?.id);

      const CanChangeTaskStatus = filteredPermissionsList?.['TASK']?.find(
        (item) => item?.title === PERMISSIONS.CAN_CHANGE_TASK_STATUS,
      );

      clonedData = clonedData?.filter((id) => id != CanChangeTaskStatus?.id);

      const CanChangeOthersTaskStatus = filteredPermissionsList?.['TASK']?.find(
        (item) => item?.title === PERMISSIONS.CAN_CHANGE_OTHERS_TASK_STATUS,
      );

      clonedData = clonedData?.filter(
        (id) => id != CanChangeOthersTaskStatus?.id,
      );
    }

    return clonedData;
  };

  const updateTemplatesPermissionsState = (data) => {
    let clonedData = data;
    const CanAccessTemplates = filteredPermissionsList?.['CHECKLIST']?.find(
      (item) => item?.title === PERMISSIONS.CAN_MANAGE_LOG_SUBMISSION,
    );

    const isCanAccessTemplates = data?.includes(CanAccessTemplates?.id);
    if (!isCanAccessTemplates) {
      const CanManageTemplates = filteredPermissionsList?.['CHECKLIST']?.find(
        (item) => item?.title === PERMISSIONS.CAN_MANAGE_CHECKLIST,
      );

      clonedData = clonedData?.filter((id) => id != CanManageTemplates?.id);

      const CanViewAllSubmissions = filteredPermissionsList?.[
        'CHECKLIST'
      ]?.find((item) => item?.title === PERMISSIONS.CAN_VIEW_ALL_SUBMISSIONS);

      clonedData = clonedData?.filter((id) => id != CanViewAllSubmissions?.id);

      const CanViewOwnSubmissions = filteredPermissionsList?.[
        'CHECKLIST'
      ]?.find((item) => item?.title === PERMISSIONS.CAN_VIEW_OWN_SUBMISSIONS);

      clonedData = clonedData?.filter((id) => id != CanViewOwnSubmissions?.id);

      const CanViewOwnAndOtherLocationsSubmissions = filteredPermissionsList?.[
        'CHECKLIST'
      ]?.find(
        (item) =>
          item?.title === PERMISSIONS.CAN_VIEW_OWN_AND_LOCATION_SUBMISSIONS,
      );

      clonedData = clonedData?.filter(
        (id) => id != CanViewOwnAndOtherLocationsSubmissions?.id,
      );
    }

    return clonedData;
  };
  const updateUsersPermissionsState = (data) => {
    let clonedData = data;
    const CanManageUser = filteredPermissionsList?.['USER']?.find(
      (item) => item?.title === PERMISSIONS.CAN_MANAGE_USERS,
    );
    const isCanManageUser = data?.includes(CanManageUser?.id);
    if (!isCanManageUser) {
      const CanMangeUserPasswords = filteredPermissionsList?.['USER']?.find(
        (item) => item?.title === PERMISSIONS.CAN_MANAGE_USERS_PASSWORD,
      );
      clonedData = clonedData?.filter((id) => id != CanMangeUserPasswords?.id);
    }
    return clonedData;
  };

  const updateWorkRequestPermissionsState = (data) => {
    const group = 'TASK_REUQEST';
    let clonedData = data;
    const CanAccessRequests = filteredPermissionsList?.[group]?.find(
      (item) => item?.title === PERMISSIONS.CAN_VIEW_TASK_REQUESTS,
    );

    const isCanAccessRequests = data?.includes(CanAccessRequests?.id);
    if (!isCanAccessRequests) {
      const CanOnlyViewOwnRequests = filteredPermissionsList?.[group]?.find(
        (item) => item?.title === PERMISSIONS.CAN_VIEW_OWN_TASK_REQUESTS_ONLY,
      );

      clonedData = clonedData?.filter((id) => id != CanOnlyViewOwnRequests?.id);

      const CanViewOwnAndOthersRequests = filteredPermissionsList?.[
        group
      ]?.find(
        (item) =>
          item?.title ===
          PERMISSIONS.CAN_VIEW_TASK_REQUESTS_IN_AUTHORIZED_LOCATIONS,
      );

      clonedData = clonedData?.filter(
        (id) => id != CanViewOwnAndOthersRequests?.id,
      );

      const CanViewAllRequests = filteredPermissionsList?.[group]?.find(
        (item) => item?.title === PERMISSIONS.CAN_VIEW_ALL_TASK_REQUESTS,
      );

      clonedData = clonedData?.filter((id) => id != CanViewAllRequests?.id);
    }

    const CanOnlyViewOwnRequests = filteredPermissionsList?.[group]?.find(
      (item) => item?.title === PERMISSIONS.CAN_VIEW_OWN_TASK_REQUESTS_ONLY,
    );

    const CanViewOwnAndOthersRequests = filteredPermissionsList?.[group]?.find(
      (item) =>
        item?.title ===
        PERMISSIONS.CAN_VIEW_TASK_REQUESTS_IN_AUTHORIZED_LOCATIONS,
    );

    const CanViewAllRequests = filteredPermissionsList?.[group]?.find(
      (item) => item?.title === PERMISSIONS.CAN_VIEW_ALL_TASK_REQUESTS,
    );

    const isNotExists =
      !data?.includes(CanOnlyViewOwnRequests?.id) &&
      !data?.includes(CanViewOwnAndOthersRequests?.id) &&
      !data?.includes(CanViewAllRequests?.id);

    if (isNotExists || !isCanAccessRequests) {
      const CanCreateRequests = filteredPermissionsList?.[group]?.find(
        (item) => item?.title === PERMISSIONS.CAN_REQUEST_TASK,
      );

      clonedData = clonedData?.filter((id) => id != CanCreateRequests?.id);

      const CanAcceptDeclineRequesst = filteredPermissionsList?.[group]?.find(
        (item) => item?.title === PERMISSIONS.CAN_ACCEPT_DECLINE_TASK_REQUEST,
      );

      clonedData = clonedData?.filter(
        (id) => id != CanAcceptDeclineRequesst?.id,
      );

      const CanConfigurePublicRequests = filteredPermissionsList?.[group]?.find(
        (item) => item?.title === PERMISSIONS.CAN_CONFIGURE_PUBLIC_TASK_REQUEST,
      );

      clonedData = clonedData?.filter(
        (id) => id != CanConfigurePublicRequests?.id,
      );
    }

    return clonedData;
  };

  const updateApprovalsPermissions = (data, permission, isChecked) => {
    let clonedData = data;

    const canViewAllApprovals = permissionsList?.['APPROVALS']?.find(
      (item) => item?.title === PERMISSIONS.CAN_VIEW_ALL_APPROVALS,
    );

    const CanViewAllApprovalsByLocations = permissionsList?.['APPROVALS']?.find(
      (item) =>
        item?.title ===
        PERMISSIONS.CAN_VIEW_ALL_APPROVALS_IN_AUTHORIZED_LOCATIONS,
    );

    if (permission === canViewAllApprovals?.id && isChecked) {
      clonedData = clonedData?.filter(
        (id) => id != CanViewAllApprovalsByLocations?.id,
      );
    }

    if (permission === CanViewAllApprovalsByLocations?.id && isChecked) {
      clonedData = clonedData?.filter((id) => id != canViewAllApprovals?.id);
    }

    return clonedData;
  };

  const updateViewSubmissionsPermissions = (data, permission, isChecked) => {
    let clonedData = data;

    const canViewAllSubmissions = permissionsList?.['CHECKLIST']?.find(
      (item) => item?.title === PERMISSIONS.CAN_VIEW_ALL_SUBMISSIONS,
    );

    const CanViewOwnAndOtherLocationsSubmissions = permissionsList?.[
      'CHECKLIST'
    ]?.find(
      (item) =>
        item?.title === PERMISSIONS.CAN_VIEW_OWN_AND_LOCATION_SUBMISSIONS,
    );

    const CanViewOwnSubmissions = permissionsList?.['CHECKLIST']?.find(
      (item) => item?.title === PERMISSIONS.CAN_VIEW_OWN_SUBMISSIONS,
    );

    if (permission === canViewAllSubmissions?.id && isChecked) {
      clonedData = clonedData?.filter(
        (id) =>
          id != CanViewOwnAndOtherLocationsSubmissions?.id &&
          id != CanViewOwnSubmissions?.id,
      );
    }

    if (
      permission === CanViewOwnAndOtherLocationsSubmissions?.id &&
      isChecked
    ) {
      clonedData = clonedData?.filter(
        (id) =>
          id != canViewAllSubmissions?.id && id != CanViewOwnSubmissions?.id,
      );
    }

    if (permission === CanViewOwnSubmissions?.id && isChecked) {
      clonedData = clonedData?.filter(
        (id) =>
          id != canViewAllSubmissions?.id &&
          id != CanViewOwnAndOtherLocationsSubmissions?.id,
      );
    }

    return clonedData;
  };

  const NoActionApplyPermissions = (permission, isChecked) => {
    const canViewAllSubmissions = permissionsList?.['CHECKLIST']?.find(
      (item) => item?.title === PERMISSIONS.CAN_VIEW_ALL_SUBMISSIONS,
    );

    const CanViewOwnAndOtherLocationsSubmissions = permissionsList?.[
      'CHECKLIST'
    ]?.find(
      (item) =>
        item?.title === PERMISSIONS.CAN_VIEW_OWN_AND_LOCATION_SUBMISSIONS,
    );

    const CanViewOwnSubmissions = permissionsList?.['CHECKLIST']?.find(
      (item) => item?.title === PERMISSIONS.CAN_VIEW_OWN_SUBMISSIONS,
    );

    const canCreateAndManageOwnAnnouncements = permissionsList?.[
      'ANNOUNCEMENTS'
    ]?.find(
      (item) =>
        item?.title === PERMISSIONS.CAN_CREATE_AND_MANAGE_OWN_ANNOUNCEMENTS,
    );

    const canManageAllAnnouncements = permissionsList?.['ANNOUNCEMENTS']?.find(
      (item) => item?.title === PERMISSIONS.CAN_MANAGE_ALL_ANNOUNCEMENTS,
    );

    if (permission === canViewAllSubmissions?.id && !isChecked) {
      return true;
    }

    if (
      permission === CanViewOwnAndOtherLocationsSubmissions?.id &&
      !isChecked
    ) {
      return true;
    }

    if (permission === CanViewOwnSubmissions?.id && !isChecked) {
      return true;
    }

    if (permission === canCreateAndManageOwnAnnouncements?.id && !isChecked) {
      return true;
    }

    if (permission === canManageAllAnnouncements?.id && !isChecked) {
      return true;
    }

    return false;
  };

  const updateAnnouncementsPermissionsState = (data, permission, isChecked) => {
    let clonedData = data;
    const group = 'ANNOUNCEMENTS';

    // Find permission IDs
    const canAccessAnnouncements = filteredPermissionsList?.[group]?.find(
      (item) => item?.title === PERMISSIONS.CAN_ACCESS_ANNOUNCEMENTS,
    );

    const canCreateAndManageOwn = filteredPermissionsList?.[group]?.find(
      (item) =>
        item?.title === PERMISSIONS.CAN_CREATE_AND_MANAGE_OWN_ANNOUNCEMENTS,
    );

    const canCreateForAnyUser = filteredPermissionsList?.[group]?.find(
      (item) =>
        item?.title === PERMISSIONS.CAN_CREATE_ANNOUNCEMENTS_FOR_ANY_USER,
    );

    const canManageAll = filteredPermissionsList?.[group]?.find(
      (item) => item?.title === PERMISSIONS.CAN_MANAGE_ALL_ANNOUNCEMENTS,
    );

    // If CAN_ACCESS_ANNOUNCEMENTS is unchecked, remove all other announcement permissions
    if (permission === canAccessAnnouncements?.id && !isChecked) {
      clonedData = clonedData?.filter(
        (id) =>
          id !== canCreateAndManageOwn?.id &&
          id !== canCreateForAnyUser?.id &&
          id !== canManageAll?.id,
      );
    }

    if (permission === canAccessAnnouncements?.id && isChecked) {
      clonedData.push(canManageAll?.id);
    }

    // // Handle toggle between CAN_CREATE_AND_MANAGE_OWN_ANNOUNCEMENTS and CAN_MANAGE_ALL_ANNOUNCEMENTS
    if (permission === canCreateAndManageOwn?.id && isChecked) {
      clonedData = clonedData?.filter((id) => id !== canManageAll?.id);
    }

    if (permission === canManageAll?.id && isChecked) {
      clonedData = clonedData?.filter(
        (id) =>
          id !== canCreateAndManageOwn?.id && id !== canCreateForAnyUser?.id,
      );
    }

    // If CAN_MANAGE_ALL_ANNOUNCEMENTS or CAN_CREATE_AND_MANAGE_OWN_ANNOUNCEMENTS is not checked,
    // CAN_CREATE_ANNOUNCEMENTS_FOR_ANY_USER shouldn't be allowed
    const hasManagePermission =
      clonedData.includes(canManageAll?.id) ||
      clonedData.includes(canCreateAndManageOwn?.id);

    if (!hasManagePermission) {
      clonedData = clonedData?.filter((id) => id !== canCreateForAnyUser?.id);
    }

    return clonedData;
  };

  const subChangeHandler = (
    permission: Permission,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const isChecked = event.target.checked;

    const isNoApplyAction = NoActionApplyPermissions(permission, isChecked);

    if (isNoApplyAction) return;

    let updatedList = isChecked
      ? union([permission], selectedState)
      : difference(selectedState, [permission]);

    updatedList = updateTasksPermissionsState(updatedList);
    updatedList = updateTemplatesPermissionsState(updatedList);
    updatedList = updateUsersPermissionsState(updatedList);
    updatedList = updateWorkRequestPermissionsState(updatedList);
    updatedList = updateWorkOrdersPermissionsState(
      updatedList,
      filteredPermissionsList,
      'WORK_ORDER',
    );
    updatedList = updatePermissionsState(
      updatedList,
      'LOCATION',
      filteredPermissionsList,
    );
    updatedList = updatePermissionsState(
      updatedList,
      'FACILITIES',
      filteredPermissionsList,
    );
    updatedList = updateAnnouncementsPermissionsState(
      updatedList,
      permission,
      isChecked,
    );

    const canManageLogSubmission = permissionsList?.['CHECKLIST']?.find(
      (item) => item?.title === PERMISSIONS.CAN_MANAGE_LOG_SUBMISSION,
    );

    if (permission === canManageLogSubmission?.id && isChecked) {
      const canViewOwnSubmissions = permissionsList?.['CHECKLIST']?.find(
        (item) => item?.title === PERMISSIONS.CAN_VIEW_OWN_SUBMISSIONS,
      );
      updatedList.push(canViewOwnSubmissions?.id);
    }

    updatedList = updateViewSubmissionsPermissions(
      updatedList,
      permission,
      isChecked,
    );

    updatedList = updateViewWorkOrdersPermissions(
      updatedList,
      permission,
      isChecked,
      permissionsList,
      'WORK_ORDER',
    );

    updatedList = updateApprovalsPermissions(
      updatedList,
      permission,
      isChecked,
    );

    setSelectedState(updatedList);
    onSelectedChange?.(updatedList);
  };

  const isAllSelected = (parentKey: string): boolean => {
    return every(permissionsList[parentKey], (item) =>
      includes(selectedState, item.id),
    );
  };

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchTerm = event?.target?.value?.toLowerCase();
      setSearchedTerm(searchTerm);
      const filteredList = {};

      // Filter permissionsList based on search term
      Object.keys(permissionsList).forEach((key) => {
        const filteredItems = permissionsList[key].filter((item) =>
          item.name.toLowerCase().includes(searchTerm),
        );
        if (filteredItems.length > 0) {
          filteredList[key] = filteredItems;
        }
      });

      setFilteredPermissionsList(filteredList);
    },
    [permissionsList],
  );

  const isRadio = (permission) => {
    return [
      TASKS_PERMISSIONS_ORDER,
      WORK_REQUESTS_PERMISSIONS_ORDER,
      TEMPLATES_PERMISSIONS_ORDER,
      ANNOUNCEMENTS_PERMISSIONS_ORDER,
      WORK_ORDERS_PERMISSIONS_ORDER,
    ].some((PERMISSIONS) => PERMISSIONS[permission?.title]?.type === 'radio');
  };

  const isSubSubPermission = (permission) => {
    return ANNOUNCEMENTS_PERMISSIONS_ORDER[permission?.title]
      ?.isSubSubPermission;
  };

  const isDisablePermission = (permission, group) => {
    const permissionOrdersHash = {
      TASK_REUQEST: WORK_REQUESTS_PERMISSIONS_ORDER,
      USER: USER_PERMISSIONS_ORDER,
      CHECKLIST: TEMPLATES_PERMISSIONS_ORDER,
      TASK: TASKS_PERMISSIONS_ORDER,
      ANNOUNCEMENTS: ANNOUNCEMENTS_PERMISSIONS_ORDER,
      WORK_ORDER: WORK_ORDERS_PERMISSIONS_ORDER,
      ROUTING_RULES: ROUTING_RULES_PERMISSIONS_ORDER,
      LOCATION: LOCATIONS_PERMISSIONS_ORDER,
    };
    const permissionOrder = permissionOrdersHash?.[group];
    const allPermissionsSelected = filteredPermissionsList?.[group]?.every(
      (item) => selectedState?.includes(item?.id),
    );

    if (allPermissionsSelected) {
      return false;
    }

    if (
      permissionOrder?.[permission?.title]?.disableBy?.length === 0 &&
      permissionOrder?.[permission?.title]?.enableBy?.length === 0
    ) {
      return false;
    }

    const disableByStatus = permissionOrder?.[
      permission?.title
    ]?.disableBy?.some((p) => {
      const permissionId = filteredPermissionsList?.[group]?.find(
        (item) => item?.title === p,
      )?.id;

      const isSelected = selectedState?.includes(permissionId);
      return isSelected;
    });

    if (disableByStatus) {
      return true;
    }

    const enableByStatus = permissionOrder?.[permission?.title]?.enableBy?.some(
      (p) => {
        const selectedPermission: any = Object.values(
          filteredPermissionsList || {},
        )
          ?.flat()
          ?.find((item: any) => item?.title === p);

        const isSelected = selectedState?.includes(selectedPermission?.id);

        return isSelected;
      },
    );

    if (enableByStatus) {
      return false;
    } else return true;
  };

  const renderPermissionItem = (item, key) => {
    let isDisabled = false;
    if (
      [
        'TASK',
        'CHECKLIST',
        'USER',
        'TASK_REUQEST',
        'ANNOUNCEMENTS',
        'WORK_ORDER',
        'ROUTING_RULES',
        'LOCATION',
      ].includes(key)
    ) {
      isDisabled = isDisablePermission(item, key);
    }

    const radio = isRadio(item) || false;
    const subSubPermission = isSubSubPermission(item) || false;
    if (isEditable) {
      isDisabled = true;
    }

    return (
      <>
        <InnerBanner item={item} />
        <SubPermission
          key={item.id}
          direction="row"
          gap="5px"
          alignItems="start"
          style={{
            marginLeft: subSubPermission ? 54 : 18,
          }}
        >
          <CustomCheckbox
            edge="start"
            radio={radio}
            checked={selectedState.includes(item.id)}
            disableRipple
            disabled={isDisabled}
            alignItems={'flex-start'}
            onChange={(event) => subChangeHandler(item.id, event)}
          />

          <Stack gap="1px">
            <Typography
              className="title"
              style={{
                ...(isDisabled && {
                  color: 'gray',
                }),
              }}
            >
              {item?.name}
            </Typography>
            <Typography
              className="description"
              style={{
                ...(isDisabled && {
                  color: 'gray',
                }),
              }}
            >
              {item?.description}
            </Typography>
          </Stack>
        </SubPermission>
      </>
    );
  };

  const renderPermissionHeader = (key) => {
    return (
      <MainPermission direction="row" gap="5px" alignItems="center">
        {![
          'TASK',
          'CHECKLIST',
          'APPROVALS',
          'TASK_REUQEST',
          'ANNOUNCEMENTS',
          'WORK_ORDER',
          'LOCATION',
          'FACILITIES',
          'ROUTING_RULES',
        ].includes(key) && (
          <CustomCheckbox
            edge="start"
            checked={isAllSelected(key)}
            disableRipple
            disabled={isEditable}
            alignItems={'flex-start'}
            onChange={(event) => parentChangeHandler(key, event)}
          />
        )}
        <Typography className="content">
          {PERMISSIONS_ENTITY_KEYWORDS[key]}
        </Typography>
      </MainPermission>
    );
  };

  const renderHierarchicalPermissions = (items, level = 0) => {
    if (!items || items.length === 0) return null;
    return items?.map((item) => {
      if (item?.type === 'HEADER') {
        return (
          <div key={item?.title} style={{ marginLeft: level * 10 }}>
            <MainPermission direction="row" gap="5px" alignItems="center">
              <Typography className="content">
                {PERMISSIONS_ENTITY_KEYWORDS?.[item?.key]}
              </Typography>
            </MainPermission>
            {item?.permissions &&
              renderHierarchicalPermissions(item?.permissions, level + 1)}
          </div>
        );
      } else if (item?.type === 'PERMISSION') {
        // Find the corresponding permission item from filteredPermissionsList
        const permissionItem = Object.values(filteredPermissionsList)
          ?.flat()
          ?.find((perm: any) => perm.title === item.title);

        if (permissionItem) {
          return (
            <div key={item.title} style={{ marginLeft: level * 2 }}>
              {renderPermissionItem(permissionItem, item.group)}
            </div>
          );
        }
        return null;
      } else if (Array.isArray(item)) {
        // Handle case where item is an array (nested permissions)
        return renderHierarchicalPermissions(item, level);
      }
      return null;
    });
  };

  return (
    <div>
      {isSearch && ( // Render search box only if isSearch is true
        <SearchBox>
          <CustomInput
            id="customRolesPermissions"
            suppressErrorSpace={true}
            grayInput={false}
            fieldProps={{
              onChange: handleInputChange,
              startAdornment: <SearchIcon />,
              placeholder: 'Type to Search',
            }}
            label=""
          />
        </SearchBox>
      )}

      {searchedTerm && Object.keys(filteredPermissionsList)?.length === 0 ? (
        <EmptyCnt>
          <NoRecordsFound />
        </EmptyCnt>
      ) : (
        <PermissionsListingCnt sx={{ height }}>
          {Object.keys(filteredPermissionsList).map((key) => {
            if (HIDE_LINEAR_GROUPS_WITH_FEATURE_FLAG?.[key]) {
              const featureFlag = HIDE_LINEAR_GROUPS_WITH_FEATURE_FLAG?.[key];
              const hasFeatureFlag = hasFeature(featureFlag);
              if (!hasFeatureFlag) return null;
            }

            return HIDE_LINEAR_GROUPS.includes(key) && !searchedTerm ? (
              <></>
            ) : (
              <div key={key}>
                {key === 'FACILITIES' && !searchedTerm ? (
                  renderHierarchicalPermissions(PERMISSIONS_HIERARCHY)
                ) : (
                  <>
                    {renderPermissionHeader(key)}
                    {filteredPermissionsList[key].map((item) => {
                      return renderPermissionItem(item, key);
                    })}
                  </>
                )}
              </div>
            );
          })}
        </PermissionsListingCnt>
      )}
    </div>
  );
};

export default CustomRolesSetPermissionsListing;
